import { useState } from 'react';
import { format } from 'date-fns';
import {
  Plus,
  Calendar,
  MapPin,
  TrendingUp,
  Pencil,
  Trash2,
  Download,
  Upload,
  Home,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Event, Property, RevenueManagerBranch } from '@/types/revenue-manager';
import { usePermissions } from '@/hooks/use-permissions';

interface EventsManagerProps {
  property: Property;
  events: Event[];
  properties: Property[];
  branches: RevenueManagerBranch[];
  currentPropertyId: string;
  onCreateEvent: () => void;
  onEditEvent: (event: Event) => void;
  onDeleteEvent: (eventId: string) => void;
  onExport: () => void;
  onImport: () => void;
}

const categoryIcons: Record<string, string> = {
  conference: '🎤',
  festival: '🎉',
  holiday: '🎄',
  sports: '⚽',
  other: '📅',
};

const impactColors: Record<string, string> = {
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
};

export function EventsManager({
  property,
  events,
  properties,
  branches,
  currentPropertyId,
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  onExport,
  onImport,
}: EventsManagerProps) {
  const { canWrite, canDelete } = usePermissions();
  const [filter, setFilter] = useState<'current' | 'multi' | 'all'>('current');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{ id: string; name: string } | null>(null);
  console.log("branches", branches)
  // Find the branch ID for the current property (currentPropertyId is id)
  const currentBranch = branches.find((b) => String(b.id) === currentPropertyId);
  const currentBranchId = String(currentBranch?.id || '');
  console.log(currentBranchId, currentBranch, events)
  // Filter events by branch ID (events store branch IDs in propertyIds)
  const currentPropertyEvents = events.filter((e) =>
    e.propertyIds.includes(currentBranchId)
  );
  
  // Group events by name, category, and location to identify multi-property events
  const eventGroups = new Map<string, Event[]>();
  events.forEach((event) => {
    const key = `${event.name}|${event.category}|${event.location || ''}`;
    if (!eventGroups.has(key)) {
      eventGroups.set(key, []);
    }
    eventGroups.get(key)!.push(event);
  });

  // Multi-property events are those where multiple events share same name, category, and location
  const multiPropertyEvents = events.filter((event) => {
    const key = `${event.name}|${event.category}|${event.location || ''}`;
    const group = eventGroups.get(key);
    return group && group.length > 1;
  });

  // Debug: Log multi-property events with their details
  console.log('Multi-Property Events (grouped by name/category/location):', 
    Array.from(eventGroups.entries())
      .filter(([_, group]) => group.length > 1)
      .map(([key, group]) => ({
        key,
        count: group.length,
        events: group.map(e => ({
          id: e.id,
          name: e.name,
          category: e.category,
          location: e.location,
          propertyIds: e.propertyIds
        }))
      }))
  );

  const filteredEvents =
    filter === 'current'
      ? currentPropertyEvents
      : filter === 'multi'
      ? multiPropertyEvents
      : events;
  const currentProperty = properties.find((p) => p.id === currentPropertyId);

  const handleDeleteClick = (event: Event) => {
    setEventToDelete({ id: event.id, name: event.name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (eventToDelete) {
      onDeleteEvent(eventToDelete.id);
    }
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Local Events Management</h2>
              <p className="text-sm text-muted-foreground">
                Manage events that impact pricing across your properties
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" onClick={onImport} disabled={!canWrite}>
              <Upload className="mr-2 h-4 w-4" />
              Import
            </Button>
            <Button onClick={onCreateEvent} disabled={!canWrite}>
              <Plus className="mr-2 h-4 w-4" />
              Create Event
            </Button>
          </div>
        </div>

        <Tabs
          value={filter}
          onValueChange={(v) => setFilter(v as typeof filter)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="current">
              Current Property ({currentPropertyEvents.length})
            </TabsTrigger>
            <TabsTrigger value="multi">
              Multi-Property ({multiPropertyEvents.length})
            </TabsTrigger>
            <TabsTrigger value="all">All Events ({events.length})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mx-auto mb-4">
            <Calendar className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No Events Found</h3>
          <p className="text-muted-foreground">
            No events for {currentProperty?.name || 'this property'}
          </p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={onCreateEvent}
            disabled={!canWrite}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create First Event
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredEvents.map((event) => {
            // Map event propertyIds (which are branch IDs) to branch names
            const eventBranches = branches.filter((b) =>
              event.propertyIds.includes(String(b.id))
            );
            const eventProperties = properties.filter((p) =>
              event.propertyIds.includes(p.id)
            );
            const priceChange = ((event.pricingMultiplier - 1) * 100).toFixed(0);

            return (
              <div
                key={event.id}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-xl">
                      {categoryIcons[event.category] || '📅'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{event.name}</h3>
                        <Badge className={impactColors[event.impact]}>
                          {event.impact.toUpperCase()} Impact
                        </Badge>
                        <Badge variant="outline">{event.category}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(event.startDate), 'MMM dd, yyyy')} →{' '}
                          {format(new Date(event.endDate), 'MMM dd, yyyy')}
                        </div>
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          +{priceChange}% price
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                          </div>
                        )}
                      </div>
                      {event.description && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {event.description}
                        </p>
                      )}
                      <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                        <Home className="h-3 w-3" />
                        {eventBranches.length > 0 
                          ? eventBranches.map((b) => b.branch_name).join(', ')
                          : eventProperties.map((p) => p.name).join(', ')}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {canWrite && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditEvent(event)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDeleteClick(event)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Event</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{eventToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
